<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <CardHeader
                :title="$t('commissions.configurations.form.texts.title')"
            />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-currency-field
                            v-model="model.licenses"
                            v-bind="$t('int-config')"
                            :label="
                                $t(
                                    'commissions.configurations.index.table.licenses',
                                )
                            "
                            :hint="
                                $t(
                                    'commissions.configurations.form.hint.licenses',
                                )
                            "
                            :error-messages="licensesErrors"
                            @input="$v.model.licenses.$touch()"
                            @blur="$v.model.licenses.$touch()"
                        ></v-currency-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-currency-field
                            v-model="model.periods"
                            v-bind="$t('int-config')"
                            :label="
                                $t(
                                    'commissions.configurations.index.table.periods',
                                )
                            "
                            :hint="
                                $t(
                                    'commissions.configurations.form.hint.periods',
                                )
                            "
                            :error-messages="periodsErrors"
                            @input="$v.model.periods.$touch()"
                            @blur="$v.model.periods.$touch()"
                        ></v-currency-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-select
                            v-model="model.commission_cycle"
                            :items="commissionCycleOptions"
                            item-text="label"
                            item-value="value"
                            :label="
                                $t(
                                    'commissions.configurations.index.table.commission_cycle',
                                )
                            "
                            :hint="
                                $t(
                                    'commissions.configurations.form.hint.commission_cycle',
                                )
                            "
                            :error-messages="commissionCycleErrors"
                            clearable
                            @input="$v.model.commission_cycle.$touch()"
                            @blur="$v.model.commission_cycle.$touch()"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-currency-field
                            v-model="model.fixed_category_payment_limit"
                            v-bind="$t('int-config')"
                            :label="
                                $t(
                                    'commissions.configurations.index.table.fixed_category_payment_limit',
                                )
                            "
                            :hint="
                                $t(
                                    'commissions.configurations.form.hint.fixed_category_payment_limit',
                                )
                            "
                            :error-messages="fixedCategoryPaymentLimitErrors"
                            clearable
                            @input="
                                $v.model.fixed_category_payment_limit.$touch()
                            "
                            @blur="
                                $v.model.fixed_category_payment_limit.$touch()
                            "
                        ></v-currency-field>
                    </v-col>
                </v-row>
            </v-card-text>
            <FormActions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import { validationMixin } from 'vuelidate'
import { between } from 'vuelidate/lib/validators'

import BaseModel from '@/models/BaseModel'
import CommissionCycle from '@/enums/CommissionCycle'

export default {
    props: {
        configuration: {
            type: Object,
            required: true,
            validator: configuration => {
                return configuration instanceof BaseModel
            },
        },
    },

    data: () => {
        return {
            loading: false,
            amountRules: 0,
        }
    },

    components: {
        CardHeader,
        FormActions,
    },

    mixins: [validationMixin],

    validations() {
        return {
            model: {
                licenses: {
                    between: between(1, 500),
                },
                periods: {
                    between: between(0, 12),
                },
                fixed_category_payment_limit: {
                    between: between(0, 60),
                },
            },
        }
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('commissions.configurations.form.bus.save-success'),
            )
        },

        clear() {
            this.$refs.form.reset()
        },
    },

    computed: {
        model() {
            return this.configuration
        },

        licensesErrors() {
            const errors = []
            if (!this.$v.model.licenses.$dirty) return errors

            !this.$v.model.licenses.between &&
                errors.push(
                    this.$t(
                        'commissions.configurations.form.errors.licenses-between',
                    ),
                )

            return errors
        },

        periodsErrors() {
            const errors = []
            if (!this.$v.model.periods.$dirty) return errors

            !this.$v.model.periods.between &&
                errors.push(
                    this.$t(
                        'commissions.configurations.form.errors.periods-between',
                    ),
                )

            return errors
        },

        fixedCategoryPaymentLimitErrors() {
            const errors = []
            if (!this.$v.model.fixed_category_payment_limit.$dirty)
                return errors

            !this.$v.model.fixed_category_payment_limit.between &&
                errors.push(
                    this.$t(
                        'commissions.configurations.form.errors.fixed-category-payment-limit-between',
                    ),
                )

            return errors
        },

        commissionCycleOptions() {
            return CommissionCycle.getOptions()
        },
    },
}
</script>
